(() => {
	/** @type {string} */
	const STORAGE_KEY = 'market_choice';
	/** @type {string} */
	const COOKIE = 'market_choice';
	/** @type {number} */
	const STORAGE_DAYS = 365;

	/** @type {string} */
	const FALLBACK_REGION_CODE = 'GB';

	/** @type {string} */
	const IP_API = 'https://free.freeipapi.com/api/json';
	/** @type {string} */
	const IP_CACHE_KEY = 'geo_ip_data_v1';
	/** @type {number} */
	const IP_CACHE_DAYS = 7;

	/**
	 * @typedef {Object} MarketEntry
	 * @property {string} country_code - ISO 3166-1 alpha-2 country code
	 * @property {string} language_key - BCP 47 language tag (e.g. "en-GB")
	 * @property {string} domain       - Destination domain URL, optionally containing {{path}}
	 * @property {string} title        - Modal heading text
	 * @property {string} body_html    - Modal body HTML
	 * @property {string} cta_switch   - Label for the "switch market" button
	 * @property {string} cta_stay     - Label for the "stay here" button
	 */

	/**
	 * @typedef {Object} MarketConfig
	 * @property {string}        ukCountryCode - Country code treated as the home/default market
	 * @property {MarketEntry[]} markets       - All configured market entries
	 */

	/**
	 * @typedef {Object} GeoData
	 * @property {string} countryCode  - ISO 3166-1 alpha-2 country code
	 * @property {string} countryName  - Full country name
	 * @property {string} regionName   - Region / province name
	 * @property {string} regionCode   - Region code (e.g. "NIR" for Northern Ireland)
	 */

	/**
	 * @typedef {Object} DetectedMarket
	 * @property {string|null}      code  - Resolved country/region code, or null on failure
	 * @property {MarketEntry|null} entry - Matching market entry, or null if none found
	 */

	const cookieManager = {
		/**
		 * Writes a cookie with the given name, value and expiry duration.
		 * @param {string} name  - Cookie name
		 * @param {string} value - Cookie value (will be URI-encoded)
		 * @param {number} days  - Days until the cookie expires
		 * @returns {void}
		 */
		set(name, value, days) {
			const expiresAt = new Date(Date.now() + days * 864e5);
			document.cookie = `${name}=${encodeURIComponent(
				value
			)};expires=${expiresAt.toUTCString()};path=/;SameSite=Lax`;
		},

		/**
		 * Reads a cookie value by name.
		 * @param {string} name - Cookie name to look up
		 * @returns {string|null} URI-decoded cookie value, or null if not found
		 */
		get(name) {
			const allCookies = document.cookie.split('; ');
			for (const cookie of allCookies) {
				const [key, ...value] = cookie.split('=');
				if (key === name) return decodeURIComponent(value.join('='));
			}
			return null;
		},

		/**
		 * Persists a market choice to both localStorage and a long-lived cookie.
		 * Only call this when a user actively declines a redirect (stay / close).
		 * Do NOT call on follow-through redirects — those are intentionally not persisted.
		 * @param {string} market - Choice value to store (e.g. "STAY_GB", "CLOSE")
		 * @returns {void}
		 */
		saveChoice(market) {
			try {
				localStorage.setItem(STORAGE_KEY, JSON.stringify({ value: market, timestamp: Date.now() }));
			} catch {}
			cookieManager.set(COOKIE, market, STORAGE_DAYS);
		},

		/**
		 * Returns the previously stored market choice, checking localStorage first then cookies.
		 * @returns {string|null} The stored choice value, or null if none exists
		 */
		getChoice() {
			try {
				const item = localStorage.getItem(STORAGE_KEY);
				if (item) {
					const parsed = JSON.parse(item);
					return parsed?.value || null;
				}
			} catch {}
			return cookieManager.get(COOKIE);
		},

		/**
		 * Reads and parses the market configuration JSON embedded in the page
		 * via the `#market-prompts` script element.
		 * @returns {MarketConfig|null} Parsed config object, or null on failure
		 */
		parseConfig() {
			try {
				const el = document.getElementById('market-prompts');
				if (!el) return null;
				return JSON.parse(el.textContent || 'null');
			} catch {
				return null;
			}
		},
	};

	/**
	 * Fetches geolocation data for the current visitor's IP address.
	 * Results are cached in sessionStorage for the duration of the tab session.
	 * @returns {Promise<GeoData|null>} Geolocation data, or null on error
	 */
	const fetchIPData = async () => {
		try {
			const cachedRaw = sessionStorage.getItem(IP_CACHE_KEY);
			if (cachedRaw) {
				const cached = JSON.parse(cachedRaw);
				const ts = Number(cached?.timestamp || 0);
				const ageMs = Date.now() - ts;
				if (ts && ageMs >= 0 && ageMs < IP_CACHE_DAYS * 864e5) return cached.data || null;
			}
		} catch {}

		try {
			const response = await fetch(IP_API, { method: 'GET' });
			if (!response.ok) return null;

			const data = await response.json();
			try {
				sessionStorage.setItem(IP_CACHE_KEY, JSON.stringify({ timestamp: Date.now(), data }));
			} catch {}
			return data;
		} catch {
			return null;
		}
	};

	/**
	 * Extracts the lowercase hostname from a raw domain string.
	 * Strips any `{{path}}` placeholder before parsing so hostname comparison is accurate.
	 * @param {string|null|undefined} raw - Raw domain value from a market entry
	 * @returns {string} Lowercase hostname, or an empty string if parsing fails
	 */
	const getHostname = (raw) => {
		if (!raw) return '';
		let url = String(raw).trim();

		if (url.includes('{{path}}')) url = url.replace('{{path}}', '');
		if (!/^https?:\/\//i.test(url)) url = 'https://' + url;

		try {
			return new URL(url).hostname.toLowerCase();
		} catch {
			return '';
		}
	};

	/** @type {string} Lowercase hostname of the current page */
	const currentHost = window.location.hostname.toLowerCase();

	/**
	 * Returns true if the given market entry's domain matches the current site,
	 * including subdomain relationships.
	 * @param {MarketEntry|null|undefined} entry - Market entry to check
	 * @returns {boolean}
	 */
	const isCurrentSite = (entry) => {
		const target = getHostname(entry?.domain);
		if (!target) return false;
		if (currentHost === target) return true;
		if (currentHost.endsWith('.' + target)) return true;
		return false;
	};

	/** @type {HTMLElement|null} */
	const modal = document.getElementById('market-redirect-modal');

	const elements = {
		/** @type {HTMLElement|null} */
		title: modal?.querySelector('[data-market-title]') ?? null,
		/** @type {HTMLElement|null} */
		body: modal?.querySelector('[data-market-body]') ?? null,
		/** @type {HTMLButtonElement|null} */
		btnSwitch: modal?.querySelector('[data-market-switch]') ?? null,
		/** @type {HTMLButtonElement|null} */
		btnStay: modal?.querySelector('[data-market-stay]') ?? null,
		/** @type {HTMLButtonElement|null} */
		btnClose: modal?.querySelector('[data-market-close]') ?? null,
		/** @type {HTMLElement|null} */
		countdown: modal?.querySelector('[data-market-countdown]') ?? null,
	};

	/** @type {ReturnType<typeof setInterval>|null} */
	let countdownTimer = null;
	/** @type {number} */
	let countdownDeadline = 0;
	/** @type {number} Total seconds for the auto-redirect countdown */
	const countdownSecs = 10;

	/**
	 * Updates the countdown element's visible text to reflect the remaining seconds.
	 * @param {number} s - Remaining seconds
	 * @returns {void}
	 */
	const updateCountdownText = (s) => {
		if (!elements.countdown) return;
		elements.countdown.textContent =
			s > 0 ? `Auto-redirecting you in ${s} second${s === 1 ? '' : 's'}…` : 'Redirecting…';
	};

	/**
	 * Clears the active countdown interval and resets the countdown element's display.
	 * @returns {void}
	 */
	const stopCountdown = () => {
		if (countdownTimer) {
			clearInterval(countdownTimer);
			countdownTimer = null;
		}
		if (elements.countdown) elements.countdown.style.display = '';
	};

	/**
	 * Shows the redirect modal.
	 * @returns {void}
	 */
	const open = () => {
		if (!modal) return;
		modal.hidden = false;
		modal.setAttribute('aria-hidden', 'false');
		document.body.classList.add('no-scroll');
	};

	/**
	 * Hides the redirect modal and stops any active countdown.
	 * @returns {void}
	 */
	const close = () => {
		if (!modal) return;
		stopCountdown();
		modal.hidden = true;
		modal.setAttribute('aria-hidden', 'true');
		document.body.classList.remove('no-scroll');
	};

	/**
	 * Navigates to the destination domain for the given market entry.
	 * No choice is persisted here — rejection choices are stored by their own handlers.
	 * @param {MarketEntry} entry - Market entry to redirect to
	 * @returns {void}
	 */
	const doRedirect = (entry) => {
		if (!entry?.domain) return;

		let url = entry.domain;

		if (url.includes('{{path}}')) {
			url = url.replace('{{path}}', window.location.pathname + window.location.search);
		}

		window.location.href = url;
	};

	/**
	 * Starts a countdown timer that auto-redirects to the given market entry on expiry.
	 * Clears any previously running countdown before starting.
	 * @param {MarketEntry} entry - Market entry to redirect to when the countdown ends
	 * @returns {void}
	 */
	const startCountdown = (entry) => {
		stopCountdown();
		if (!elements.countdown || !entry?.domain) return;

		countdownDeadline = Date.now() + countdownSecs * 1000;
		updateCountdownText(countdownSecs);

		countdownTimer = setInterval(() => {
			const remaining = Math.max(0, Math.ceil((countdownDeadline - Date.now()) / 1000));
			updateCountdownText(remaining);
			if (remaining <= 0) {
				stopCountdown();
				doRedirect(entry);
			}
		}, 1000);

		elements.countdown.style.display = '';
	};

	/**
	 * Populates the modal with content from the given market entry and wires up
	 * all button and keyboard event handlers.
	 * @param {MarketEntry} entry - Market entry whose content should be displayed
	 * @returns {boolean} True if the modal was hydrated successfully, false if modal or entry is missing
	 */
	const hydrate = (entry) => {
		if (!modal || !entry) return false;

		if (elements.title) elements.title.textContent = entry.title || '';
		if (elements.body) {
			elements.body.innerHTML = typeof entry.body_html === 'string' ? entry.body_html : '';
		}

		if (elements.btnSwitch) {
			elements.btnSwitch.textContent = entry.cta_switch || 'Yes, please';
			elements.btnSwitch.onclick = (e) => {
				e.preventDefault();
				stopCountdown();
				doRedirect(entry);
			};
		}

		if (elements.btnStay) {
			elements.btnStay.textContent = entry.cta_stay || 'No, thanks';
			elements.btnStay.onclick = () => {
				cookieManager.saveChoice('STAY_' + FALLBACK_REGION_CODE);
				close();
			};
		}

		if (elements.btnClose) {
			elements.btnClose.onclick = () => {
				cookieManager.saveChoice('CLOSE');
				close();
			};
		}

		if (elements.countdown) {
			if (entry.domain) startCountdown(entry);
			else {
				elements.countdown.textContent = '';
				elements.countdown.style.display = 'none';
			}
		}

		window.addEventListener('keyup', (event) => event.code === 'Escape' && close(), { once: true });

		return true;
	};

	/** @type {MarketConfig} */
	const config = cookieManager.parseConfig() || { ukCountryCode: 'GB', markets: [] };

	/** @type {Record<string, MarketEntry>} */
	const byCountry = {};
	/** @type {Array<{prefix: string, entry: MarketEntry}>} */
	const byLangPrefix = [];

	(config.markets || []).forEach((m) => {
		if (!m) return;

		const cc = String(m.country_code || '').toUpperCase();
		if (cc) byCountry[cc] = m;

		const lk = String(m.language_key || '').toLowerCase();
		if (lk) {
			const prefix = lk.split('-')[0] || lk;
			byLangPrefix.push({ prefix, entry: m });
		}
	});

	/**
	 * Attempts to find a matching market entry using the browser's preferred language list.
	 * @returns {MarketEntry|null} First matching market entry, or null if none found
	 */
	const langFallback = () => {
		const langs = (navigator.languages || [navigator.language])
			.filter(Boolean)
			.map((l) => String(l).toLowerCase());

		for (const l of langs) {
			const prefix = l.split('-')[0];
			const hit = byLangPrefix.find((r) => r.prefix === prefix);
			if (hit) return hit.entry;
		}
		return null;
	};

	/**
	 * Normalises a value to a trimmed lowercase string.
	 * @param {unknown} v
	 * @returns {string}
	 */
	const normalizeLower = (v) =>
		String(v || '')
			.trim()
			.toLowerCase();

	/**
	 * Normalises a value to a trimmed uppercase string.
	 * @param {unknown} v
	 * @returns {string}
	 */
	const normalizeUpper = (v) =>
		String(v || '')
			.trim()
			.toUpperCase();

	/**
	 * Resolves a GB sub-region code (IE or NI) via IP geolocation when the primary
	 * detected country matches the configured UK country code.
	 * @returns {Promise<'IE'|'NI'|null>} Sub-region code, or null if not applicable
	 */
	const resolveGbSubRegionCode = async () => {
		const geo = await fetchIPData();
		if (!geo) return null;

		const countryName = normalizeLower(geo.countryName);
		const countryCode = normalizeUpper(geo.countryCode);

		if (countryCode === 'IE' || countryName === 'ireland') return 'IE';

		const regionName = normalizeLower(geo.regionName);
		const regionCode = normalizeUpper(geo.regionCode);

		if (regionCode === 'NIR' || regionName === 'northern ireland') return 'NI';

		return null;
	};

	const w = /** @type {any} */ (window);
	/** @type {Record<string, any>} */
	const MarketSwitcher = (w.MarketSwitcher = w.MarketSwitcher || {});

	MarketSwitcher.getChoice = cookieManager.getChoice;
	MarketSwitcher.saveChoice = cookieManager.saveChoice;

	/**
	 * Prompts the user to switch to the market for the given country code by showing
	 * the redirect modal. Does nothing if the entry doesn't exist or is the current site.
	 * @param {string} cc - Two-letter ISO country code
	 * @returns {boolean} True if the modal was shown, false otherwise
	 */
	MarketSwitcher.promptFor = (cc) => {
		const code = normalizeUpper(cc);
		const entry = byCountry[code];
		if (!entry || isCurrentSite(entry)) return false;
		hydrate(entry);
		open();
		return true;
	};

	/**
	 * Immediately redirects to the market for the given country code without prompting.
	 * @param {string} cc - Two-letter ISO country code
	 * @returns {void}
	 */
	MarketSwitcher.switchTo = (cc) => {
		const entry = byCountry[normalizeUpper(cc)];
		if (entry?.domain && !isCurrentSite(entry)) {
			stopCountdown();
			doRedirect(entry);
		}
	};

	MarketSwitcher.close = close;
	MarketSwitcher.open = open;
	MarketSwitcher.isCurrentSite = isCurrentSite;

	/**
	 * Returns the market entry for the given country code, or null if not configured.
	 * @param {string} cc - Two-letter ISO country code
	 * @returns {MarketEntry|null}
	 */
	MarketSwitcher.getEntry = (cc) => byCountry[normalizeUpper(cc)] || null;

	/**
	 * Detects the visitor's country via Shopify's browsing context API, then refines
	 * GB results to NI or IE via IP geolocation where applicable.
	 * @returns {Promise<DetectedMarket>}
	 */
	MarketSwitcher.getDetected = async () => {
		const ukCode = normalizeUpper(config.ukCountryCode || 'GB');

		try {
			const res = await fetch('/browsing_context_suggestions.json', { credentials: 'same-origin' });
			const data = await res.json();
			const country = data?.detected_values?.country || null;
			let code = normalizeUpper(country?.handle || country?.code || '') || null;

			if (!code) code = FALLBACK_REGION_CODE;

			if (code === ukCode) {
				const sub = await resolveGbSubRegionCode();
				if (sub) code = sub;
			}

			if (code === 'NI' && !byCountry.NI && byCountry.IE) code = 'IE';
			if (!code) code = FALLBACK_REGION_CODE;

			return { code, entry: byCountry[code] || null };
		} catch {
			return { code: null, entry: null };
		}
	};

	if (cookieManager.getChoice()) return;

	/* Bail out for automated tools, importers, headless browsers, and explicit opt-out */
	const params = new URLSearchParams(window.location.search);
	if (params.has('noredirect') || params.has('nr')) return;
	if (navigator.webdriver) return;

	fetch('/browsing_context_suggestions.json', { credentials: 'same-origin' })
		.then((r) => r.json())
		.then(async (data) => {
			const country = data?.detected_values?.country || null;
			let code = normalizeUpper(country?.handle || country?.code || '') || null;

			if (!code) code = FALLBACK_REGION_CODE;

			const ukCode = normalizeUpper(config.ukCountryCode || 'GB');
			if (code === ukCode) {
				const sub = await resolveGbSubRegionCode();
				if (sub) code = sub;
			}

			if (code === 'NI' && !byCountry.NI && byCountry.IE) {
				code = 'IE';
			}

			if (!code) code = FALLBACK_REGION_CODE;

			if (code === ukCode) {
				const lf = langFallback();
				if (lf && !isCurrentSite(lf)) {
					if (hydrate(lf)) open();
				}
				return;
			}

			const entry = byCountry[code];
			if (!entry || isCurrentSite(entry)) return;
			if (hydrate(entry)) open();
		})
		.catch(() => {
			const fb = langFallback();
			if (fb && !isCurrentSite(fb)) {
				if (hydrate(fb)) open();
			}
		});
})();
